import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { FieldState, FormState } from 'formstate';
import { useWrapRequest } from 'use-wrap-request';

import { Api } from '@core/api';
import { useModel, wrapInFormField } from '@core/forms';
import { injectTSDI } from '@core/tsdi';
import { links } from '@core/router';
import { useImageUpload } from '@components/hooks/use-image-upload';
import {
    ImageType,
    TeamAccess,
    TeamCreateSupportRequest,
    TeamLeaderboardVisibility,
    TeamMemberVisibility
} from '@api/ChallengeClient';

const NAME_MAX = 60;
const DESCRIPTION_MAX = 500;

export function useCreateTeamStore(communityId: number) {
    const { challengeClient } = injectTSDI(Api);
    const navigate = useNavigate();

    // Abort the in-flight team-create POST on unmount so navigating away
    // mid-request cancels the fetch (no orphan team is created server-side).
    const abortRef = useRef<AbortController | null>(null);
    useEffect(() => {
        return () => abortRef.current?.abort();
    }, []);

    const createRequest = useWrapRequest(
        async (data: TeamCreateSupportRequest) => {
            abortRef.current = new AbortController();
            try {
                const result =
                    await challengeClient.support.teamSupportControllerCreateTeam(
                        communityId,
                        data,
                        { signal: abortRef.current.signal }
                    );
                return result;
            } finally {
                abortRef.current = null;
            }
        },
        {
            successMessage: ([data]) =>
                `Team "${data.name}" was created successfully`
        }
    );

    const model = useModel(() => {
        const form = new FormState({
            name: new FieldState<string>('').validators(
                (v) => (v.trim() ? false : 'Name is required'),
                (v) =>
                    v.trim().length > NAME_MAX
                        ? `Maximum ${NAME_MAX} characters`
                        : false
            ),
            description: new FieldState<string>('').validators(
                (v) => (v.trim() ? false : 'Description is required'),
                (v) =>
                    v.trim().length > DESCRIPTION_MAX
                        ? `Maximum ${DESCRIPTION_MAX} characters`
                        : false
            ),
            image: new FormState({
                bucketName: new FieldState<string>('').validators((v) =>
                    v ? false : 'Image is required'
                ),
                objectKey: new FieldState<string>('').validators((v) =>
                    v ? false : 'Image is required'
                )
            }),
            access: new FieldState<TeamAccess>(TeamAccess.OPEN),
            memberVisibility: new FieldState<TeamMemberVisibility>(
                TeamMemberVisibility.FULL
            ),
            leaderboardVisibility: new FieldState<TeamLeaderboardVisibility>(
                TeamLeaderboardVisibility.FULL
            ),
            managerUserId: new FieldState<string>('').validators((v) =>
                v.trim() ? false : 'Manager user ID is required'
            ),
            // Stored as string in the form so empty input stays empty;
            // parsed to number | undefined at submit. Regex enforces ≥ 1.
            memberLimitInput: new FieldState<string>('').validators((v) =>
                v && !/^[1-9]\d*$/.test(v)
                    ? 'Must be a positive integer'
                    : false
            )
        });

        return {
            form,
            fields: {
                name: wrapInFormField(form.$.name),
                description: wrapInFormField(form.$.description),
                image: {
                    bucketName: wrapInFormField(form.$.image.$.bucketName),
                    objectKey: wrapInFormField(form.$.image.$.objectKey)
                },
                access: wrapInFormField(form.$.access),
                memberVisibility: wrapInFormField(form.$.memberVisibility),
                leaderboardVisibility: wrapInFormField(
                    form.$.leaderboardVisibility
                ),
                managerUserId: wrapInFormField(form.$.managerUserId),
                memberLimitInput: wrapInFormField(form.$.memberLimitInput)
            }
        };
    });

    const imageUpload = useImageUpload(ImageType.TEAM, (cdn) => {
        model.fields.image.bucketName.onChange(cdn.bucketName);
        model.fields.image.objectKey.onChange(cdn.objectKey);
    });

    const onSubmitHandler = async () => {
        const result = await model.form.validate();
        if (result.hasError) {
            return;
        }

        const { fields } = model;
        const memberLimitRaw = fields.memberLimitInput.value.trim();

        const data: TeamCreateSupportRequest = {
            name: fields.name.value.trim(),
            description: fields.description.value.trim(),
            image: {
                bucketName: fields.image.bucketName.value,
                objectKey: fields.image.objectKey.value
            },
            access: fields.access.value,
            memberVisibility: fields.memberVisibility.value,
            leaderboardVisibility: fields.leaderboardVisibility.value,
            managerUserId: fields.managerUserId.value.trim(),
            ...(memberLimitRaw ? { memberLimit: Number(memberLimitRaw) } : {})
        };

        const response = await createRequest.request([data]);
        if (response) {
            navigate(links.communityTeams(String(communityId)));
        }
    };

    const onCancelHandler = () => {
        navigate(links.communityTeams(String(communityId)));
    };

    return {
        fields: model.fields,
        onSubmitHandler,
        onCancelHandler,
        createRequest,
        imageUpload
    };
}
